// DirectoryPage — 조직도. 다우 동기화 부서 트리 + 인원, 관리자 조직 대시보드.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import { directoryAPI } from '../api/client';
import useIsMobile from '../hooks/useIsMobile';
import { IconArrowLeft, IconMinus, IconPlus, IconRefresh, IconSitemap } from '@tabler/icons-react';
import { card as cardBase } from '../utils/uiStyles';
import { Page, PageHeader, Button, SearchInput, Avatar, EmptyState, LoadingState, ErrorState } from '../components/ui';
import { alertDialog } from '../components/AppDialog';

// 직급(직위) 정렬 순서 — 높을수록 앞. 목록에 없는 값은 맨 뒤(그들끼리 이름 가나다).
const POS_ORDER = ['회장', '부회장', '사장', '부사장', '전무', '상무', '이사', '이사대우', '실장', '본부장', '수석', '부장', '차장', '과장', '대리', '주임', '사원', '인턴'];
function posRank(p) {
  const s = (p || '').replace(/\s/g, '');
  for (let i = 0; i < POS_ORDER.length; i++) { if (s.indexOf(POS_ORDER[i]) !== -1) return i; }
  return POS_ORDER.length;  // 미상 → 맨 뒤
}

// 겸직 표시(2026-08-18) — 다우는 한 사람이 2개 이상 부서에 속할 수 있다(실측 12명).
function orgLabel(e) {
  const names = (e.orgs && e.orgs.length) ? e.orgs.map((o) => o.name) : (e.org ? [e.org] : []);
  return [...new Set(names.filter(Boolean))].join(' · ');
}
function dutyLabel(e) {
  // 직책은 부서마다 다르다(백암FC=총괄센터장 / 양지FC=없음) → 부서명을 같이 보여야 뜻이 통한다
  const rows = (e.orgs || []).filter((o) => o.duty);
  if (!rows.length) return e.duty;
  if (rows.length === 1 && !(e.orgs.length > 1)) return rows[0].duty;
  return rows.map((o) => `${o.duty} (${o.name})`).join(' · ');
}

// 부서 트리 + 각 부서에 소속 인원 부착. 이름 dedupe, parent_name 계층, 인원 0 하위트리 숨김.
function buildTree(depts, emps) {
  const byOrg = {};
  // 겸직(2개 이상 부서)이면 소속된 부서 모두에 나와야 한다. orgs 가 없으면 대표 소속으로 폴백.
  emps.forEach((e) => {
    const names = (e.orgs && e.orgs.length) ? e.orgs.map((o) => o.name) : (e.org ? [e.org] : []);
    [...new Set(names.filter(Boolean))].forEach((n) => { (byOrg[n] = byOrg[n] || []).push(e); });
  });
  const byName = {};
  depts.forEach((d) => { if (!byName[d.name]) byName[d.name] = { name: d.name, parent: '' }; if (d.parent && !byName[d.name].parent) byName[d.name].parent = d.parent; });
  const childrenOf = {};
  Object.values(byName).forEach((n) => {
    const hp = n.parent && byName[n.parent] && n.parent !== n.name;
    (childrenOf[hp ? n.parent : '__root__'] = childrenOf[hp ? n.parent : '__root__'] || []).push(n);
  });
  const seen = new Set();
  const attach = (n) => {
    if (seen.has(n.name)) return { ...n, children: [], members: [], subtotal: 0 };
    seen.add(n.name);
    const members = (byOrg[n.name] || []).slice().sort((a, b) => {
      const ra = posRank(a.position || a.duty), rb = posRank(b.position || b.duty);
      if (ra !== rb) return ra - rb;                       // 1순위: 직급
      return (a.name || '').localeCompare(b.name || '', 'ko');  // 2순위: 이름
    });
    const children = (childrenOf[n.name] || []).filter((c) => c.name !== n.name).map(attach);
    const subtotal = members.length + children.reduce((s, c) => s + c.subtotal, 0);
    return { ...n, members, children, subtotal };
  };
  return (childrenOf['__root__'] || []).map(attach);
}
export default function DirectoryPage() {
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin = user.role === 'admin';
  const [q, setQ] = useState('');
  const [emps, setEmps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());   // 열린 부서명(기본 전부 접힘)
  const [sel, setSel] = useState(null);             // 선택 인원
  const [syncing, setSyncing] = useState(false);
  const mobile = useIsMobile();                     // 모바일: 트리↔인원상세 마스터/디테일 드릴다운

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadErr('');
    try {
      const [er, dr, sr] = await Promise.all([
        directoryAPI.employees('', ''),
        directoryAPI.departments(),
        directoryAPI.status(),
      ]);
      setEmps(er.data.employees || []);
      setDepts(dr.data.departments || []);
      setStatus(sr.data);
    } catch (e) {
      setEmps([]);
      setDepts([]);
      setLoadErr('조직도를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const roots = useMemo(() => buildTree(depts, emps), [depts, emps]);
  // 첫 데이터 로드 시 최상단 루트(아워박스)만 1회 펼침(이후 토글 자유)
  const didInitExpand = useRef(false);
  useEffect(() => {
    if (!didInitExpand.current && roots.length > 0) {
      didInitExpand.current = true;
      setExpanded(new Set(roots.map((r) => r.name)));
    }
  }, [roots]);
  const openSet = expanded;
  const toggle = (name) => { const s = new Set(openSet); s.has(name) ? s.delete(name) : s.add(name); setExpanded(s); };

  const doSync = async () => {
    setSyncing(true);
    try { await directoryAPI.sync(); await loadAll(); }
    catch (e) { alertDialog('동기화 실패: ' + (e?.response?.data?.detail || '')); }
    setSyncing(false);
  };
  const fmtTime = (iso) => { if (!iso) return '-'; try { return new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }); } catch { return iso; } };
  const title = (e) => e.position || e.duty || '';

  // 검색 결과(평면)
  const searchHits = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return null;
    return emps.filter((e) => [e.name, e.org, e.duty, e.position, e.email, e.mobile, e.login_id].map((x) => (x || '').toLowerCase()).join(' ').includes(ql));
  }, [q, emps]);

  const row = (active) => ({ display: 'flex', alignItems: 'center', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', background: active ? 'var(--gw-blue-soft, #eaf3fc)' : 'transparent' });

  // 트리 노드 렌더(부서 → 인원/하위부서)
  const renderDept = (node, depth) => {
    if (node.subtotal === 0) return null;
    const open = openSet.has(node.name);
    const pad = depth * 16 + 8;
    return (
      <div key={'d:' + node.name + ':' + depth}>
        <div onClick={() => toggle(node.name)} style={{ display: 'flex', alignItems: 'center', padding: '5px 8px', paddingLeft: pad + 'px', cursor: 'pointer', borderRadius: '6px' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--gw-hover, #efefed)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
          <span style={{ width: '14px', height: '14px', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '3px', color: 'var(--gw-secondary, #64635f)', marginRight: '7px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{open ? <IconMinus size={11} stroke={2} /> : <IconPlus size={11} stroke={2} />}</span>
          <span style={{ fontSize: '13px', fontWeight: depth === 0 ? 700 : 600, color: 'var(--gw-text, #32312e)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
          <span style={{ marginLeft: '7px', fontSize: '11px', color: 'var(--gw-muted, #92918d)' }}>{node.subtotal}</span>
        </div>
        {open && (
          <div>
            {node.members.map((m) => (
              <div key={'p:' + m.login_id} onClick={() => setSel(m)} style={{ ...row(sel && sel.login_id === m.login_id), paddingLeft: (pad + 22) + 'px' }}
                onMouseEnter={(e) => { if (!(sel && sel.login_id === m.login_id)) e.currentTarget.style.background = 'var(--gw-hover, #efefed)'; }}
                onMouseLeave={(e) => { if (!(sel && sel.login_id === m.login_id)) e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ marginRight: '6px', display: 'inline-flex' }}><Avatar name={m.name || m.login_id} size={19} /></span>
                <span style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.login_id}{title(m) ? <span style={{ color: 'var(--gw-muted, #92918d)', marginLeft: '5px' }}>{title(m)}</span> : null}{m.status === 'STOP' && <span style={{ color: 'var(--gw-red-ink, #a4564d)', fontSize: '11px', marginLeft: '4px' }}>(정지)</span>}</span>
              </div>
            ))}
            {node.children.map((c) => renderDept(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const detail = (e) => (
    <div style={{ ...cardBase, padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
        <span style={{ marginRight: '12px', display: 'inline-flex' }}><Avatar name={e.name || e.login_id} size={42} /></span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '18px', fontWeight: 620, color: 'var(--gw-text, #32312e)', wordBreak: 'break-word' }}>{e.name || e.login_id}{e.status === 'STOP' && <span style={{ color: 'var(--gw-red-ink, #a4564d)', fontSize: '12px', marginLeft: '6px' }}>(정지)</span>}</div>
          <div style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)' }}>{[e.org, e.duty || e.position].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      {[['부서', orgLabel(e)], ['직책', dutyLabel(e)], ['직위', e.position], ['이메일', e.email, true], ['연락처', e.mobile]].map(([k, v, mail], i) => (
        <div key={i} style={{ display: 'flex', padding: '8px 0', borderTop: '1px solid var(--gw-line, #eeeeec)', fontSize: '13px' }}>
          <span style={{ width: '64px', color: 'var(--gw-muted, #92918d)', flexShrink: 0 }}>{k}</span>
          <span style={{ color: 'var(--gw-text, #32312e)', minWidth: 0, wordBreak: 'break-word' }}>{v ? (mail ? <a href={`mailto:${v}`} style={{ color: 'var(--gw-blue, #2383e2)', textDecoration: 'none', wordBreak: 'break-word' }}>{v}</a> : v) : '-'}</span>
        </div>
      ))}
    </div>
  );

  return (
    <AppShell app="directory">
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: 'var(--gw-bg, #fff)' }}>
        <Announcements scope="directory" />
        <Page>
          <PageHeader
            icon={IconSitemap}
            tone="red"
            title="조직도"
            subtitle={(
              <>
                임직원 {status ? status.employees : '-'}명 · 부서 {status ? status.departments : '-'}개
                {status?.last_synced ? ` · 최종 동기화 ${fmtTime(status.last_synced)}` : ''}{status && !status.enabled ? ' · (비활성)' : ''}
              </>
            )}
            actions={isAdmin ? (
              <Button variant="primary" icon={syncing ? undefined : IconRefresh} onClick={doSync} disabled={syncing}>{syncing ? '동기화 중…' : '동기화'}</Button>
            ) : null}
          />

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* 좌: 조직도 트리(부서 펼치면 인원 인라인) / 검색 시 평면 결과 */}
            <div style={{ ...cardBase, padding: 0, width: mobile ? '100%' : '380px', flexShrink: 0, overflow: 'auto', maxHeight: mobile ? 'none' : 'calc(var(--gw-app-h, 100vh) - 244px)', display: (mobile && sel) ? 'none' : 'block' }}>
              <div style={{ padding: '8px', borderBottom: '1px solid var(--gw-line, #eeeeec)', position: 'sticky', top: 0, background: 'var(--gw-surface, #fff)', zIndex: 1 }}>
                <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름/아이디/부서/직위/직책/전화" width="100%" />
              </div>
              <div style={{ padding: '6px 4px' }}>
                {loading ? (
                  <LoadingState>불러오는 중…</LoadingState>
                ) : loadErr ? (
                  <div style={{ padding: '10px' }}><ErrorState message={loadErr} /></div>
                ) : searchHits ? (
                  <>
                    <div style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', padding: '4px 10px' }}>검색 결과 {searchHits.length}명</div>
                    {searchHits.map((m) => (
                      <div key={m.login_id} onClick={() => setSel(m)} style={{ ...row(sel && sel.login_id === m.login_id), paddingLeft: '12px' }}>
                        <span style={{ marginRight: '6px', display: 'inline-flex' }}><Avatar name={m.name || m.login_id} size={19} /></span>
                        <span style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.login_id}<span style={{ color: 'var(--gw-muted, #92918d)', marginLeft: '6px' }}>{[m.org, title(m)].filter(Boolean).join(' · ')}</span></span>
                      </div>
                    ))}
                    {searchHits.length === 0 && <EmptyState description="결과 없음" />}
                  </>
                ) : roots.length > 0 ? roots.map((r) => renderDept(r, 0)) : (
                  <EmptyState description="표시할 조직 정보가 없습니다." />
                )}
              </div>
            </div>

            {/* 우: 선택 인원 상세 (모바일: 인원 선택 시 전체폭, '← 조직도'로 복귀) */}
            <div style={{ flex: 1, minWidth: mobile ? 0 : '300px', width: mobile ? '100%' : undefined, display: (mobile && !sel) ? 'none' : 'block' }}>
              {sel ? (
                <>
                  {mobile && (
                    <Button variant="secondary" size="sm" icon={IconArrowLeft} onClick={() => setSel(null)} style={{ marginBottom: '10px' }}>조직도</Button>
                  )}
                  {detail(sel)}
                </>
              ) : (
                <div style={{ ...cardBase, padding: 0 }}>
                  <EmptyState description={<>왼쪽 조직도에서 부서를 펼쳐 이름을 누르면<br />상세 정보(부서·직책·직위·이메일·연락처)가 표시됩니다.</>} />
                </div>
              )}
            </div>
          </div>
        </Page>
      </div>
    </AppShell>
  );
}
